import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { Check, ChevronsUpDown, Pencil, Trash2, RefreshCw, Plus, ClipboardList, Package, Truck, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtDate = (val) => (val ? String(val).split('T')[0] : '');
const fmtNum = (val) => (val === null || val === undefined || val === '' ? '-' : Number(val).toLocaleString('en-IN'));

// Configuration for each category: display columns + editable fields + endpoints
const buildConfig = (suppliers, items) => ({
  material_reconciliation: {
    label: 'Material Reconciliation',
    icon: ClipboardList,
    color: 'text-sky-600',
    columns: [
      { key: 'type', label: 'Type' },
      { key: 'supplier_name', label: 'Supplier' },
      { key: 'invoice_number', label: 'Invoice' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'invoice_value', label: 'Value (₹)', type: 'num' },
      { key: 'weight', label: 'Weight', type: 'num' },
      { key: 'dc', label: 'DC' },
    ],
    fields: [
      { key: 'type', label: 'Type', type: 'select', options: [{ value: 'pro-hub', label: 'Pro-Hub' }, { value: 'container-bau', label: 'Container Bau' }] },
      { key: 'supplier_id', label: 'Supplier', type: 'select', options: suppliers.map(s => ({ value: s.id, label: s.name })) },
      { key: 'invoice_number', label: 'Invoice Number', type: 'text' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'invoice_value', label: 'Invoice Value (₹)', type: 'number' },
      { key: 'weight', label: 'Weight', type: 'number' },
      { key: 'dc', label: 'DC', type: 'text' },
      { key: 'project_number', label: 'Project Number', type: 'text' },
      { key: 'westaw_project_number', label: 'WESTAW Project Number', type: 'text' },
    ],
    putUrl: (id) => `${BACKEND_URL}/api/material-reconciliation/pro-hub/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/material-reconciliation/${id}`,
    postUrl: `${BACKEND_URL}/api/material-reconciliation/pro-hub`,
    projectField: 'westaw_project_number',
    defaults: { type: 'pro-hub' },
  },
  consumable: {
    label: 'Consumable',
    icon: Package,
    color: 'text-emerald-600',
    columns: [
      { key: 'item_name', label: 'Item' },
      { key: 'quantity', label: 'Quantity', type: 'num' },
      { key: 'employee_name', label: 'Employee' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'project', label: 'Project' },
    ],
    fields: [
      { key: 'item_id', label: 'Item', type: 'select', options: items.map(i => ({ value: i.id, label: i.name })) },
      { key: 'quantity', label: 'Quantity', type: 'number' },
      { key: 'employee_name', label: 'Employee Name', type: 'text' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'project', label: 'Project', type: 'text' },
    ],
    putUrl: (id) => `${BACKEND_URL}/api/stock-issues/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/stock-issues/${id}`,
    postUrl: `${BACKEND_URL}/api/stock-issues`,
    projectField: 'project',
  },
  job_work: {
    label: 'Job Work',
    icon: Truck,
    color: 'text-amber-600',
    columns: [
      { key: 'supplier_name', label: 'Supplier' },
      { key: 'dc', label: 'DC' },
      { key: 'invoice', label: 'Invoice' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'weight', label: 'Weight', type: 'num' },
      { key: 'value', label: 'Value (₹)', type: 'num' },
      { key: 'transporter_name', label: 'Transporter' },
      { key: 'transport_expense_send', label: 'Send (₹)', type: 'num' },
      { key: 'transport_expense_return', label: 'Return (₹)', type: 'num' },
      { key: 'remarks', label: 'Remarks' },
    ],
    fields: [
      { key: 'supplier_id', label: 'Supplier', type: 'select', options: suppliers.map(s => ({ value: s.id, label: s.name })) },
      { key: 'dc', label: 'DC', type: 'text' },
      { key: 'invoice', label: 'Invoice', type: 'text' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'weight', label: 'Weight', type: 'number' },
      { key: 'value', label: 'Value (₹)', type: 'number' },
      { key: 'transporter_name', label: 'Transporter Name', type: 'text' },
      { key: 'transport_expense_send', label: 'Transport Expense Send (₹)', type: 'number' },
      { key: 'transport_expense_return', label: 'Transport Expense Return (₹)', type: 'number' },
      { key: 'remarks', label: 'Remarks', type: 'text' },
      { key: 'westaw_project_number', label: 'WESTAW Project Number', type: 'text' },
    ],
    putUrl: (id) => `${BACKEND_URL}/api/material-reconciliation/job-work/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/material-reconciliation/${id}`,
    postUrl: `${BACKEND_URL}/api/material-reconciliation/job-work`,
    projectField: 'westaw_project_number',
  },
  turned_parts: {
    label: 'Turned Parts',
    icon: Settings2,
    color: 'text-purple-600',
    columns: [
      { key: 'part_number', label: 'Part Number' },
      { key: 'quantity', label: 'Quantity', type: 'num' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'project_number', label: 'Project' },
    ],
    fields: [
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'quantity', label: 'Quantity', type: 'number' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'project_number', label: 'Project Number', type: 'text' },
    ],
    putUrl: (id) => `${BACKEND_URL}/api/turned-parts/stock-out/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/turned-parts/stock-out/${id}`,
    postUrl: `${BACKEND_URL}/api/turned-parts/stock-out`,
    projectField: 'project_number',
  },
});

const CATEGORY_ORDER = ['material_reconciliation', 'consumable', 'job_work', 'turned_parts'];

function ProjectEntries() {
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectSelectOpen, setProjectSelectOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);

  // editing state
  const [editCat, setEditCat] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [dialogMode, setDialogMode] = useState('edit'); // 'add' | 'edit'
  const [saving, setSaving] = useState(false);

  const config = buildConfig(suppliers, items);

  useEffect(() => {
    const init = async () => {
      try {
        const [p, s, i] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/projects`, authHeaders()),
          axios.get(`${BACKEND_URL}/api/suppliers`, authHeaders()),
          axios.get(`${BACKEND_URL}/api/items`, authHeaders()),
        ]);
        setProjects(p.data);
        setSuppliers(s.data);
        setItems(i.data);
      } catch (e) {
        toast.error('Failed to load reference data');
      }
    };
    init();
  }, []);

  const fetchEntries = async (projectNumber) => {
    const proj = projectNumber ?? selectedProject;
    if (!proj) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/project-entries`, {
        ...authHeaders(),
        params: { westaw_project_number: proj },
      });
      setEntries(res.data);
    } catch (e) {
      toast.error('Failed to load project entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = (projectNumber) => {
    setSelectedProject(projectNumber);
    setProjectSelectOpen(false);
    fetchEntries(projectNumber);
  };

  const getSelectedProjectDisplay = () => {
    const project = projects.find(p => p.westaw_project_number === selectedProject);
    return project ? `${project.westaw_project_number} - ${project.client_name}` : 'Search and select a project...';
  };

  const openEdit = (cat, entry) => {
    setDialogMode('edit');
    setEditCat(cat);
    setEditEntry(entry);
    const form = {};
    config[cat].fields.forEach((f) => {
      let v = entry[f.key];
      if (f.type === 'date') v = fmtDate(v);
      if (v === null || v === undefined) v = '';
      form[f.key] = v;
    });
    setEditForm(form);
  };

  const openAdd = (cat) => {
    setDialogMode('add');
    setEditCat(cat);
    setEditEntry(null);
    const cfg = config[cat];
    const today = new Date().toISOString().split('T')[0];
    const form = {};
    cfg.fields.forEach((f) => {
      form[f.key] = f.type === 'date' ? today : '';
    });
    // pre-fill project linkage and any category defaults
    if (cfg.projectField) form[cfg.projectField] = selectedProject;
    if (cfg.defaults) Object.assign(form, cfg.defaults);
    setEditForm(form);
  };

  const closeEdit = () => {
    setEditCat(null);
    setEditEntry(null);
    setEditForm({});
    setDialogMode('edit');
  };

  const handleSave = async () => {
    if (!editCat) return;
    setSaving(true);
    try {
      const cfg = config[editCat];
      const payload = {};
      cfg.fields.forEach((f) => {
        let v = editForm[f.key];
        if (f.type === 'number') v = parseFloat(v) || 0;
        if (f.type === 'date') v = v ? `${v}T00:00:00` : null;
        payload[f.key] = v;
      });
      if (dialogMode === 'add') {
        await axios.post(cfg.postUrl, payload, authHeaders());
        toast.success(`${cfg.label} entry added`);
      } else {
        await axios.put(cfg.putUrl(editEntry.id), payload, authHeaders());
        toast.success(`${cfg.label} entry updated`);
      }
      closeEdit();
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${dialogMode === 'add' ? 'add' : 'update'} entry`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat, entry) => {
    try {
      await axios.delete(config[cat].deleteUrl(entry.id), authHeaders());
      toast.success('Entry deleted');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const renderCell = (col, entry) => {
    const v = entry[col.key];
    if (col.type === 'date') return fmtDate(v) || '-';
    if (col.type === 'num') return fmtNum(v);
    return v === null || v === undefined || v === '' ? '-' : v;
  };

  const renderSection = (cat) => {
    const cfg = config[cat];
    const rows = entries?.[cat] || [];
    const Icon = cfg.icon;
    return (
      <AccordionItem value={cat} key={cat} className="border border-slate-200 rounded-lg bg-white px-4">
        <AccordionTrigger className="hover:no-underline" data-testid={`section-${cat}`}>
          <div className="flex items-center gap-3">
            <Icon className={cn('w-5 h-5', cfg.color)} />
            <span className="font-semibold text-slate-900">{cfg.label}</span>
            <Badge variant="secondary" data-testid={`count-${cat}`}>{rows.length}</Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex justify-end pb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openAdd(cat)}
              data-testid={`add-${cat}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add {cfg.label} Entry
            </Button>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No entries for this project.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {cfg.columns.map((c) => (
                      <TableHead key={c.key}>{c.label}</TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((entry) => (
                    <TableRow key={entry.id} data-testid={`row-${cat}-${entry.id}`}>
                      {cfg.columns.map((c) => (
                        <TableCell key={c.key}>{renderCell(c, entry)}</TableCell>
                      ))}
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(cat, entry)}
                          data-testid={`edit-${cat}-${entry.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-${cat}-${entry.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. The {cfg.label} entry will be permanently removed.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDelete(cat, entry)}
                                data-testid="confirm-delete"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="project-entries-title">Project Entries</h1>
        <p className="text-slate-600 mt-2">Select a project to view, edit or delete its entries across all modules</p>
      </div>

      {/* Project selector */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-xl font-semibold">Select Project</CardTitle>
          <CardDescription>Choose a WESTAW project to manage its entries</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>WESTAW Project</Label>
              <Popover open={projectSelectOpen} onOpenChange={setProjectSelectOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={projectSelectOpen}
                    className="w-full justify-between font-normal"
                    data-testid="project-entries-select"
                  >
                    <span className={selectedProject ? 'text-slate-900' : 'text-slate-500'}>
                      {getSelectedProjectDisplay()}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search projects..." />
                    <CommandList>
                      <CommandEmpty>No project found.</CommandEmpty>
                      <CommandGroup>
                        {projects.map((project) => (
                          <CommandItem
                            key={project.id}
                            value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                            onSelect={() => handleSelectProject(project.westaw_project_number)}
                          >
                            <Check
                              className={cn('mr-2 h-4 w-4', selectedProject === project.westaw_project_number ? 'opacity-100' : 'opacity-0')}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{project.westaw_project_number}</span>
                              <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="opacity-0">Action</Label>
              <Button
                onClick={() => fetchEntries()}
                disabled={!selectedProject || loading}
                className="w-full"
                style={{ backgroundColor: '#008080' }}
                data-testid="update-refresh-button"
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                Update
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries sections */}
      {selectedProject && entries && (
        <Accordion type="multiple" className="space-y-3" data-testid="entries-accordion">
          {CATEGORY_ORDER.map((cat) => renderSection(cat))}
        </Accordion>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editCat} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'add' ? 'Add' : 'Edit'} {editCat ? config[editCat].label : ''} Entry</DialogTitle>
            <DialogDescription>
              {dialogMode === 'add'
                ? 'Fill in the fields below to add a new entry for this project.'
                : 'Update the fields below and save your changes for this entry.'}
            </DialogDescription>
          </DialogHeader>
          {editCat && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {config[editCat].fields.map((f) => (
                <div className="space-y-2" key={f.key}>
                  <Label>{f.label}</Label>
                  {f.type === 'select' ? (
                    <Select
                      value={editForm[f.key] ? String(editForm[f.key]) : ''}
                      onValueChange={(val) => setEditForm({ ...editForm, [f.key]: val })}
                    >
                      <SelectTrigger data-testid={`edit-field-${f.key}`}>
                        <SelectValue placeholder={`Select ${f.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                      value={editForm[f.key] ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      data-testid={`edit-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} data-testid="edit-cancel-button">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#008080' }} data-testid="edit-save-button">
              {saving ? 'Saving...' : dialogMode === 'add' ? 'Add Entry' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjectEntries;
